import {
  BlurFilter,
  Container,
  Graphics,
  Rectangle,
  RenderTexture,
  Sprite,
  type Renderer,
  type Texture,
  type Ticker,
} from 'pixi.js';
import { HalftoneFilter, halftoneBinaryOf } from '../bg';
import { keepAlive } from '../textureLifetime';
import { richLabel } from '../ui/richText';
import type { Text as PixiText } from 'pixi.js';
import { createWalk, type Walk } from '../../core/walk';
import { PRESS } from '../palette';
import { rect as rectPts, TAU, flatten, type Pt } from '../card/art/geom';
import type { FaceSize } from '../card/metrics';
import { bandRuns } from './stripes';


/**
 * **崩溃日志里那个行号在乱跳。**
 *
 * 随机漫步（`core/walk.ts`）—— 每帧挪一小步、方向往回偏，读起来像"寄存器在扫"，
 * 而不是每帧换一个随机数那种噪点。
 *
 * ⚠️ 整数：行号要 `Math.floor`。这个 walk 是**标准化之后**的版本，
 *    故障滤镜的强度用的是同一个模块（那边是连续值）。
 *
 * ⚠️ 它是**每个实例一份**（`#panicWalk`）：随机数要走实例自己的种子
 *    （`#panicRng`），项目里不用 `Math.random`。
 */
export function createPanicWalk(random: () => number): Walk {
  return createWalk({ min: 0, max: 192, initial: 50, maxStep: 10, snap: Math.floor, random });
}
/**
 * **濒死状态：一道贯穿屏幕中心的 45° 红斜杠横带。**
 *
 * 抽到异常牌之后，画面变成这样（从下往上叠）：
 *
 * ```
 *   ④ 异常牌        ← 悬浮在画面正中，**永远不进手牌堆**（视觉上）
 *   ③ 黑色半调阴影   ← ②上半调滤镜，染黑。比牌大得多，边缘是一圈渐散的网点
 *   ② 红色光        ← 一种"外发光"：牌的剪影 → 大范围模糊 → 染红、加色
 *   ① 警示横带       ← 45° 红斜杠，不断从画面中心向左右两边跑
 * ```
 *
 * ## ②和③是**同一片光**
 *
 * 这是这一版最关键的改动，也是评审要求的：
 *
 * ```
 *   白的牌形剪影 ──大范围模糊──▶ 一片光 ──┬──染红────────▶ ② 红色光
 *                                        └──上半调滤镜──▶ ③ 黑色半调阴影
 * ```
 *
 * **所以阴影不再比牌大一点点，它和辉光一样大。** 前一版把网点铺在一个
 * 只比牌大 7% 的矩形上，再把这坨东西模糊了当辉光 —— 阴影小得几乎只有
 * 牌边一圈。现在反过来：先有光（大面积），再对光上半调。
 *
 * 层的顺序因此变成了效果的全部：**黑阴影压在红光上面**。
 * 网点之间是透明的，红光从缝里透出来 —— 于是那团阴影读起来是
 * "一片被红光照亮的网点"，而不是一块红雾，也不是一块黑斑。
 * （黑底 UI 上纯黑的阴影本来什么也看不见，它的形状只能靠自己的红光显出来。）
 *
 * ## 它替换掉了什么（历史）
 *
 * 第一版是一整套「放射状裂纹 SDF」：裂纹网络生成 → CPU 光栅化成贴图 →
 * 一个语法不小的着色器。信息量很大，但它和「抽到致命牌」这件事**没有语义关系** ——
 * 谁来都说得出那是玻璃碎了，说不出那和这张牌有什么关系。
 * 那一套已经删掉（原来的 `src/render/shatter/`），换成了这里的横带。
 *
 * 关键差别：斜杠和异常牌底部的危险条纹是**同一个** `hazardBands`，
 * 所以牌一抽出来，屏幕上的条纹和牌上的条纹是同一套语言在说同一句话。
 */

export interface DyingStyle {
  // ---- ① 警示横带 ----
  /** 横带高度（逻辑像素）。它贯穿屏幕中心，所以太厚会盖住牌 */
  bandHeight: number;
  /** 斜杠的垂直间距 */
  bandPitch: number;
  /** 斜杠占空比 */
  bandDuty: number;
  /** 斜杠往外跑的速度（相位/秒）。1 = 每秒挪一整个间距 */
  bandSpeed: number;
  /**
   * 右半边是不是左半边的**镜像**。
   *
   * - `false`（默认）：平移 —— 两边的 45° 斜杠**倾斜方向完全相同**，只是移动方向相反
   * - `true`：镜像 —— 中心那一刀是连续的 V 形，但两边的倾斜方向相反
   *
   * 评审要的是前者，但明说了"怕反悔"，所以留这个开关。见 `stripes.ts`。
   */
  bandMirror: boolean;

  // ---- ② 光（外发光和阴影共用）----
  /** 牌形剪影比牌大多少（每边比例）。0 = 和牌一样大 */
  silhouetteGrow: number;
  /** 模糊半径（逻辑像素）。「大范围」就靠它 —— 它同时决定阴影和辉光的大小 */
  glowBlur: number;
  /** 辉光的颜色。⚠️ 在**画的时候**染，不在烘焙时 —— 改它不用重烘 */
  glowColor: number;
  /** 呼吸的最低 / 最高透明度 */
  glowAlphaMin: number;
  glowAlphaMax: number;
  /** 一次呼吸多少秒 */
  breathPeriod: number;

  // ---- ③ 黑色半调阴影（= ②上半调滤镜）----
  /** 网点节距 */
  shadowPitch: number;
  /** 网点排列角度（弧度） */
  shadowAngle: number;
  /** 网点对比度。>1 网点分得更开、黑得更透 */
  shadowContrast: number;
}

export const DEFAULT_DYING_STYLE: DyingStyle = {
  bandHeight: 54,
  bandPitch: 15,
  bandDuty: 0.5,
  bandSpeed: 0.55,
  bandMirror: false,

  silhouetteGrow: 0.02,
  glowBlur: 90,
  // ⚠️ 用印刷红：这个特效是**异常牌自己的**警报，跟着它一起出现。
  //    `PRESS.red` 的纪律是"只有白卡能用"，而这里它就是白卡在响
  glowColor: PRESS.red,
  glowAlphaMin: 0.22,
  glowAlphaMax: 0.85,
  breathPeriod: 2.4,

  // ⚠️ 网点要和背景的网点（11px / 45°）**错开**，否则两层网点会叠出摩尔纹。
  //    所以阴影用不一样的节距和角度。这条是评审提的，实测确实会叠出波纹
  shadowPitch: 5.5,
  shadowAngle: (Math.PI / 180) * 15,
  shadowContrast: 1.4,
};

/**
 * 一小节的重音往上加多少透明度。
 *
 * ⚠️ 是**加**不是乘。濒死时 `#intensity` 已经是 1，乘任何 >1 的系数都会被
 * Pixi 夹回 1 —— 于是"重音"在唯一需要它的时刻（濒死）恰好**完全看不见**。
 * 加在两处还有余量的地方：辉光的呼吸上限是 0.85，黑色阴影跟着 `a`。
 *
 * 0.3 是"一眼能看出拍子、但不像在闪警报"的量：呼吸最低点 0.22 抬到 0.52，
 * 呼吸最高点 0.85 才刚好顶到 1。
 */
const BAR_HIT_GAIN = 0.3;

/**
 * 崩溃日志的文案。`%s` = 随机十六进制（地址 / 状态码）、`%d` = 随机数。
 *
 * 三行分别是：内核崩溃、进程死掉、以及一行"地址"级的细节 —— 都是真家伙
 * （Linux 的 `Kernel panic`、Windows 的 `CRITICAL_PROCESS_DIED`、
 * 经典的 `*** STOP:`），不是随便拼的乱码。评审点名了前两个。
 *
 * ⚠️ 全大写 + 等宽字：这套设计语言里**没有**花哨的字体（`docs/ui-design.md`），
 *    警报的样子就是"机器写给人看的字"。
 */
export const PANIC_LINES: readonly string[] = [
  ':: CRITICAL FAILURE - ACCESS VIOLATION @ %s',
  ':: REMOTE HOST CONNECTION STATUS: DEGRADED',
  ':: SECTOR: %d/192 RESPONSIVE',
];

/** 崩溃日志换一行内容的间隔（毫秒）。⚠️ 别调到"每帧"：换字要重栅格化纹理 */
const PANIC_INTERVAL_MS = 140;

/**
 * **崩溃日志一行一行地出**（每行隔这么久）。
 *
 * 评审要的"威胁性"里有一条是**节奏**：三行同时出现只是"贴了张纸"，
 * 一行一行往外蹦才像机器一边报错一边死。纯函数，所以"第 0 帧只有一行、
 * 到点之后三行都在"这件事能在 node 里断言。
 */
export const PANIC_LINE_MS = 130;

/** 到这一刻为止，应该显示几行（至少一行，最多 `total` 行） */
export function panicLinesShown(elapsedMs: number, total: number, lineMs = PANIC_LINE_MS): number {
  if (total <= 0) return 0;
  if (!Number.isFinite(elapsedMs)) return total;
  const shown = Math.floor(Math.max(0, elapsedMs) / Math.max(1, lineMs)) + 1;
  return Math.max(1, Math.min(total, shown));
}

/** 重音落回去的快慢（1/秒）。和手牌脉动同一档（`DEFAULT_FEEL.barPulseRate`） */
const BAR_HIT_RATE = 7;

/**
 * 改了必须重新预渲染的参数。
 *
 * ⚠️ 它们是**烘焙期**的：剪影的模糊、阴影的网点都进了纹理。
 * 其余参数（横带、呼吸、透明度、**辉光颜色**）每帧现算，改了立刻生效。
 */
const BAKE_KEYS: readonly (keyof DyingStyle)[] = [
  'glowBlur',
  'silhouetteGrow',
  'shadowPitch',
  'shadowAngle',
  'shadowContrast',
];

export interface DyingLayerOptions {
  readonly renderer: Renderer;
  /** 逻辑画布尺寸 */
  readonly width: number;
  readonly height: number;
  /** 悬浮的那张牌**本身**的尺寸（不含出血）—— 剪影按它生成 */
  readonly card: FaceSize;
  readonly style?: Partial<DyingStyle>;
  /** 进出濒死的过渡时长（秒） */
  readonly fadeIn?: number;
  readonly fadeOut?: number;
}

export class DyingLayer {
  /** 加到场景里的东西。⚠️ 牌要加在它**后面**（上面） */
  readonly view: Container;

  /** 三个子层，调试页要单独开关它们 */
  readonly band: Graphics;
  readonly glow: Sprite;
  readonly halo: Sprite;

  #style: DyingStyle;
  #time = 0;
  #phase = 0;
  #target = 0;
  #intensity = 0;
  #pulseLeft = 0;
  /** 小节重音 0..1。和 `#pulseLeft` 无关，见 `barPulse()` */
  #barHit = 0;
  #override: number | null = null;
  #timeScale = 1;
  readonly #fadeIn: number;
  readonly #fadeOut: number;
  #fieldTexture: RenderTexture;
  #shadowTexture: RenderTexture;
  readonly #renderer: Renderer;
  readonly #cardSize: FaceSize;
  /**
   * **崩溃日志**（评审：「在红色警戒线边上添加诸如 Kernel Panic、
   * CRITICAL_PROCESS_DIED 之类的崩溃和报错日志信息」）。
   *
   * 三条等宽小字，压在横带的上下沿 —— 和斜杠是同一套"这不是装饰，是警报"的
   * 语言。内容每隔 `PANIC_INTERVAL_MS` 换一遍（**不能每帧换**：`Text` 换字要
   * 重新栅格化，每帧换一次就是每帧一次纹理上传）。
   */
  readonly #panic: PixiText[];
  readonly #panicBox: Container;
  #panicLeft = 0;
  /** 这一轮濒死开始到现在（毫秒）—— 崩溃日志的"逐行"靠它 */
  #panicAgeMs = 0;

  /** 崩溃日志那个行号的随机漫步（见 `createPanicWalk`） */
  #panicWalk: Walk | null = null;
  readonly #width: number;
  readonly #cx: number;
  readonly #cy: number;
  /**
   * **横带要铺满的那块矩形**（画布逻辑坐标）。
   *
   * ⚠️ 默认是画布本身，但窗口不是 16:10 时视口比画布大（x/y 是负数）——
   * 那几块"黑边"上其实是有背景的（标量场铺满整块视口），只有横带停在了画布边上，
   * 看起来就是"红线没有延伸到画面边缘"。见 `setViewport`。
   */
  #bandRect: { x: number; y: number; w: number; h: number; } | null = null;

  constructor(options: DyingLayerOptions) {
    this.#style = { ...DEFAULT_DYING_STYLE, ...options.style };
    this.#fadeIn = options.fadeIn ?? 0.14;
    this.#fadeOut = options.fadeOut ?? 0.4;
    this.#width = options.width;
    this.#renderer = options.renderer;
    this.#cardSize = options.card;
    this.#cx = options.width / 2;
    this.#cy = options.height / 2;

    this.view = new Container();
    this.view.label = 'dying';

    // ---- ① 警示横带（最底）+ 压在它边上那几行崩溃日志 ----
    this.#panicBox = new Container();
    this.#panicBox.label = 'dying-panic';
    this.#panic = PANIC_LINES.map((line) => {
      const text = richLabel({
        text: line,
        scale: 1,
        size: 13,
        fill: this.#style.glowColor,
        role: 'mono',
        tracking: 0.5,
      });
      text.alpha = 0.72;
      this.#panicBox.addChild(text);
      return text;
    });
    this.view.addChild(this.#panicBox);

    this.band = new Graphics();
    this.band.label = 'dying-band';
    this.band.position.set(0, this.#cy - this.#style.bandHeight / 2);
    this.view.addChild(this.band);
    this.#placePanic();
    this.#rollPanic();

    // ---- 两张纹理，都在构造时**预渲染**一次 ----
    //
    // ⚠️ 剪影烘成**白色**。`tint` 是**乘法**，把黑的东西染红只会得到黑 ——
    //    白纹理才能既当红光、又当黑阴影的来源
    this.#fieldTexture = bakeField(options.renderer, options.card, this.#style);
    this.#shadowTexture = bakeShadow(options.renderer, this.#fieldTexture, this.#style);

    // ---- ② 红色光 = 那片模糊的剪影，**画的时候**染红 ----
    //
    // ⚠️ 颜色不烘进纹理：烘焙期参数多一个就多一次重烘，而颜色是调得最勤的
    // ⚠️ 加色混合 —— 发光是"往画面里加光"，不是"盖一层红漆"
    this.glow = new Sprite(this.#fieldTexture);
    this.glow.label = 'dying-glow';
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = this.#style.glowColor;
    this.glow.position.set(this.#cx, this.#cy);
    this.view.addChild(this.glow);

    // ---- ③ 黑色半调阴影（由②上半调滤镜而来），压在光上面 ----
    //
    // ⚠️ 光是**预渲染**进纹理的，不是每帧的 filter：
    //    UI 上只需要改 `alpha`。一个全屏大小的 BlurFilter 每帧跑一遍
    //    是这个特效唯一付不起的开销，而它本来就只需要算一次。
    this.halo = new Sprite(this.#shadowTexture);
    this.halo.label = 'dying-shadow';
    this.halo.anchor.set(0.5);
    // 纹理是预乘的白点，染黑 = 把 rgb 压到 0、alpha 留着 → 黑网点
    this.halo.tint = 0x000000;
    this.halo.position.set(this.#cx, this.#cy);
    this.view.addChild(this.halo);

    this.view.visible = false;
  }

  /** 调试用：把可见强度钉死（绕过包络） */
  setIntensityOverride(value: number | null): void {
    this.#override = value;
  }

  set timeScale(value: number) {
    this.#timeScale = value;
  }

  get intensity(): number {
    return this.#intensity;
  }

  get active(): boolean {
    return this.#target > 0;
  }

  get style(): Readonly<DyingStyle> {
    return this.#style;
  }

  /**
   * 两张**预渲染**出来的纹理，给调试页看。
   *
   * ⚠️ 它们是中间产物，不是给人看的效果 —— 平时看不见（阴影的白点印在黑底上、
   * 光要染红再加色混合），只有摊平了才能判断"黑底上那团东西到底有没有形状"。
   * 归这一层所有，调用方不许 destroy。
   */
  /** 模糊后的剪影。红光就是它染了色，黑阴影就是它上了半调 */
  get fieldTexture(): Texture {
    return this.#fieldTexture;
  }

  get shadowTexture(): Texture {
    return this.#shadowTexture;
  }

  /** 进入濒死状态（持续到 `end()`）。抽到致命牌时调它 */
  begin(): void {
    this.#target = 1;
    this.#pulseLeft = 0;
    this.#barHit = 0;
    // 崩溃日志从头报：三行一行一行地出（见 `panicLinesShown`）
    this.#panicAgeMs = 0;
  }

  end(): void {
    this.#target = 0;
    this.#pulseLeft = 0;
    // 重音也要清：场上看不见的层不该留着一个"待会儿会闪一下"的余量
    this.#barHit = 0;
  }

  /**
   * 一次性闪一下
   *
   * ⚠️ 它是**"亮起来一会儿再自己关掉"**的语义：`#pulseLeft` 走完就把 `#target`
   * 按回 0。所以**不要**拿它来当"音乐拍了就敲一下" —— 那样第一下之后
   * 1.8 秒濒死层就自己关了，而人还在濒死。跟着拍子走的是 `barPulse()`。
   */
  pulse(seconds = 1.8): void {
    this.#pulseLeft = Math.max(this.#pulseLeft, seconds);
    this.#target = 1;
  }

  /**
   * **音乐拍到了，敲一下重音。**
   *
   * ⚠️ 和 `pulse()` 的关键区别：它**不碰 `#target`**，也就是"开不开"的意图
   * 完全不受影响 —— 濒死期间每小节敲一下不会把濒死层打开或关掉，
   * 只是让这一瞬间更亮。没在濒死时敲它也什么都不发生
   * （`update` 里 `#intensity` 是 0，重音乘出来还是 0）。
   *
   * 重音是**加**在透明度上的，不是乘：濒死时 `#intensity` 已经是 1，
   * 乘任何 >1 的系数都会被 Pixi 夹回 1，看不见。见 `BAR_HIT_GAIN`。
   */
  barPulse(strength = 1): void {
    this.#barHit = Math.max(this.#barHit, clamp01(strength));
  }

  /** 重音的当前值 0..1。读数与调试用 */
  get barHit(): number {
    return this.#barHit;
  }

  /** 一次性闪一下的剩余秒数。调试用 */
  get pulseLeft(): number {
    return this.#pulseLeft;
  }

  /**
   * 改参数。
   *
   * ⚠️ 有一半参数是**烘焙期**的（剪影的模糊、阴影的网点）——
   * 它们进了纹理，改了就得重新烘。调优页每次拖动都会走到这里，
   * 所以由这个方法自己判断要不要重烘，调用方不用记住哪些是哪种。
   */
  applyStyle(style: Partial<DyingStyle>): void {
    const before = this.#style;
    this.#style = { ...before, ...style };
    this.#placeBand();
    this.glow.tint = this.#style.glowColor;

    if (BAKE_KEYS.some((k) => before[k] !== this.#style[k])) this.rebake();
  }

  /** 重新预渲染剪影与阴影纹理。只有烘焙期参数变了才需要 */
  rebake(): void {
    const field = bakeField(this.#renderer, this.#cardSize, this.#style);
    this.#fieldTexture.destroy(true);
    this.#fieldTexture = field;
    this.glow.texture = field;

    const shadow = bakeShadow(this.#renderer, field, this.#style);
    this.#shadowTexture.destroy(true);
    this.#shadowTexture = shadow;
    this.halo.texture = shadow;
  }

  /**
   * 每帧推进。
   *
   * ⚠️ 和背景、卡牌一样，这个方法给**那唯一一个** ticker 回调调
   * （`architecture.md` §7.1），不要自己再 `app.ticker.add`。
   */
  update(ticker: Ticker): void {
    const dt = (ticker.deltaMS / 1000) * this.#timeScale;
    this.#time += dt;

    // ---- 包络：指数趋近，起步快收尾慢（比线性更像「亮起来 / 暗下去」）----
    if (this.#override !== null) {
      this.#intensity = this.#override;
    } else {
      if (this.#pulseLeft > 0) {
        this.#pulseLeft -= dt;
        if (this.#pulseLeft <= 0) {
          this.#pulseLeft = 0;
          this.#target = 0;
        }
      }
      const rate = this.#target > this.#intensity ? this.#fadeIn : this.#fadeOut;
      const k = rate <= 0 ? 1 : 1 - Math.exp(-dt / (rate * 0.35));
      this.#intensity += (this.#target - this.#intensity) * k;
      if (Math.abs(this.#target - this.#intensity) < 0.002) this.#intensity = this.#target;
    }

    const a = this.#intensity;
    this.view.visible = a > 0.001;
    if (!this.view.visible) return;

    // ---- ① 横带：斜杠从中心往两边跑 ----
    //
    // ⚠️ 这个**减号**不是笔误，也不是随手写的。相位增加时，条带沿法线
    //    n=(cos45,sin45) 往右下走 —— 左半边的斜杠于是**朝中心**跑。
    //    右半边在 `bandRuns` 里用的是 -phase，所以它往右下、也就是朝外跑。
    //    而"向内还是向外"在看图时完全看不出来（斜杠就是斜杠），
    //    只有对两帧做互相关、求位移方向才能定。所以方向写死在这里。
    this.#phase -= dt * this.#style.bandSpeed;
    this.#phase = ((this.#phase % 1) + 1) % 1;
    this.drawBand();
    this.band.alpha = a;

    // ---- ①b 崩溃日志：跟着整体强度一起亮，内容每隔一会儿换一遍 ----
    this.#panicAgeMs += dt * 1000;
    this.#panicLeft -= dt * 1000;
    if (this.#panicLeft <= 0) {
      this.#panicLeft = PANIC_INTERVAL_MS;
      this.#rollPanic();
    }
    const panicShown = panicLinesShown(this.#panicAgeMs, this.#panic.length);
    this.#panic.forEach((text, i) => {
      text.visible = i < panicShown;
    });
    this.#panicBox.alpha = a;

    this.halo.alpha = a;

    // ---- ② 呼吸 ----
    const breath = 0.5 - 0.5 * Math.cos((this.#time / this.#style.breathPeriod) * TAU);
    const s = this.#style;
    // 呼吸之上**再加**一下小节重音。
    //
    // ⚠️ 重音只加在**这一个**通道上，因为只有它有余量：辉光的呼吸上限是
    //    0.85（抬得动），横带和阴影都已经顶在 1 上了。加满一圈的结果是
    //    "拍子看不见" —— 而那从代码上完全看不出来
    this.glow.alpha = accentAlpha(
      s.glowAlphaMin + (s.glowAlphaMax - s.glowAlphaMin) * breath,
      this.#barHit,
      a,
    );

    // ⚠️ 衰减放在**读完** `#barHit` 之后。反过来的话，`barPulse()` 之后的第一帧
    //    就已经掉了一格 —— 硬起的那一下会被吃掉（`handView` 里同一条教训）
    if (this.#barHit > 0) {
      this.#barHit *= Math.exp(-dt * BAR_HIT_RATE);
      if (this.#barHit < 1e-3) this.#barHit = 0;
    }
  }

  /**
   * **告诉它"整块视口"有多大**（画布逻辑坐标）。
   *
   * 评审：「红线没有延伸至画面边缘。」根因是横带按**画布宽**铺（1280），
   * 而窗口不是 16:10 时视口比画布宽 —— 两边那几条黑边上背景是有的、
   * 横带却没有，看着就是"红线半路断了"。
   *
   * ⚠️ 只改横带的**宽度和左边**（`bandRuns` 从 0 开始铺，平移到 x 就是铺满
   * 那一整块）；光晕/阴影还是围着牌心，它们本来就只该在牌附近。
   */
  setViewport(quad: { x: number; y: number; w: number; h: number; }): void {
    const before = this.#bandRect;
    if (
      before !== null &&
      before.x === quad.x &&
      before.y === quad.y &&
      before.w === quad.w &&
      before.h === quad.h
    ) {
      return;
    }
    this.#bandRect = { ...quad };
    this.#placeBand();
  }

  /**
   * 把三行崩溃日志摆在横带的**上下沿**（跟着视口一起铺开）。
   *
   * 第一行压在横带上沿之上，第二、三行压在它下沿之下 —— 这样横带读起来像
   * 一条"把日志拦腰截断"的警告带，而不是飘在空中的一行字。
   */
  #placePanic(): void {
    const rect = this.#bandRect ?? { x: 0, y: 0, w: this.#width, h: 0 };
    const half = this.#style.bandHeight / 2;
    const pad = 16;
    const rows = [this.#cy - half - 20, this.#cy + half + 6, this.#cy + half + 24];
    this.#panic.forEach((text, i) => {
      text.position.set(rect.x + pad, rows[i] ?? this.#cy);
    });
    void rect.w;
  }

  /** 换一批日志内容（带随机数/地址的那种"活的"报错） */
  #rollPanic(): void {
    // ⚠️ 纯视效（见 `docs/onboarding.md` §4.4 第 5 条）：日志里那个随机地址 /
    //    行号每次都该不一样才像"活的"，不需要复现
    const random = (): number => Math.random();
    this.#panicWalk ??= createPanicWalk(random);
    const hex = (digits: number): string => Array.from({ length: digits },
      () => '0123456789ABCDEF'[Math.floor(random() * 16)] ?? '0'
    ).join('');
    const walkValue = Math.random() > 0.8 ? this.#panicWalk!.next() : this.#panicWalk.value;
    this.#panic.forEach((text, i) => {
      const template = PANIC_LINES[i] ?? '';
      // 模板里的 `%s` 是随机十六进制（地址 / 状态码），`%d` 是随机数
      text.text = template
        .replace('%s', `0x${hex(8)}`)
        .replace('%d', String(walkValue));
    });
  }

  /** 按当前视口把横带摆到位（并重画 —— 宽度变了，斜杠的片数也跟着变） */
  #placeBand(): void {
    const rect = this.#bandRect ?? { x: 0, y: 0, w: this.#width, h: 0 };
    this.band.position.set(rect.x, this.#cy - this.#style.bandHeight / 2);
    this.#placePanic();
    this.drawBand();
  }

  /** 重画横带的斜杠。一条横带一百多个小四边形，每帧重算可以忽略 */
  drawBand(): void {
    const { bandHeight, bandPitch, bandDuty, bandMirror } = this.#style;
    const width = this.#bandRect?.w ?? this.#width;
    const runs = bandRuns(width, bandHeight, this.#phase, bandPitch, bandDuty, bandMirror);
    this.band.clear();
    // ⚠️ 半透明的印刷红：这是**警示**不是实心色块，压太实会把底下的背景糊掉
    for (const poly of runs.left) {
      this.band.poly(flatten(poly)).fill({ color: this.#style.glowColor, alpha: 0.82 });
    }
    for (const poly of runs.right) {
      this.band.poly(flatten(poly)).fill({ color: this.#style.glowColor, alpha: 0.82 });
    }
  }

  destroy(): void {
    for (const f of this.glow.filters ?? []) f.destroy();
    this.glow.filters = [];
    this.view.destroy({ children: true });
    this.#fieldTexture.destroy(true);
    this.#shadowTexture.destroy(true);
  }
}

/**
 * 烘焙**那片光**：牌形剪影 → 大范围模糊。
 *
 * 它是这一版的两个图层**共同**的来源：
 *
 * ```
 *   这片光 ──染红────────▶ ② 红色外发光
 *          └──上半调滤镜──▶ ③ 黑色半调阴影
 * ```
 *
 * 所以"阴影有多大"和"辉光有多大"是同一件事，只有一个旋钮（`glowBlur`）。
 */
/**
 * 把一小节的重音加到一层的透明度上。
 *
 * - `base` —— 这一层**本来**该有的透明度（呼吸算出来的那个）
 * - `barHit` —— 重音 0..1（`barPulse()` 写进去的）
 * - `intensity` —— 濒死的总包络。**它就是"这一层亮着没有"那道闸**
 *
 * ⚠️ **加，不是乘。** 濒死时 `base` 最高到 0.85，乘任何 >1 的系数都会被 Pixi
 * 夹回 1 —— 于是重音在唯一需要它的时刻恰好**完全看不见**，而且代码读起来
 * 还挺"正确"。
 *
 * ⚠️ 闸门**必须**乘在最后。少了它，菜单里每小节敲一下就会把濒死层的辉光
 * 抬到 0.3（`view.visible` 挡住的是绘制，不是 alpha 的值）——
 * 而"重音不许把濒死层打开"是这条纪律的另一半。
 */
export function accentAlpha(base: number, barHit: number, intensity = 1): number {
  return Math.min(1, base + BAR_HIT_GAIN * clamp01(barHit)) * clamp01(intensity);
}

function clamp01(v: number): number {
  // ⚠️ NaN 要挡掉：`alpha = NaN` 在 Pixi 里是**整层不画**，
  //    而且不报错 —— 那种症状（"濒死层偶尔不见了"）没法查
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function bakeField(renderer: Renderer, card: FaceSize, style: DyingStyle): RenderTexture {
  // 留够模糊扩散的余量。2.2 倍是实测够用的：再小会在贴图边缘被切出直边
  const margin = Math.ceil(style.glowBlur * 2.2);
  const w = Math.ceil(card.width * (1 + style.silhouetteGrow * 2)) + margin * 2;
  const h = Math.ceil(card.height * (1 + style.silhouetteGrow * 2)) + margin * 2;
  const ox = margin + card.width * style.silhouetteGrow;
  const oy = margin + card.height * style.silhouetteGrow;

  const shape: Pt[] = rectPts(ox, oy, card.width, card.height);
  const g = new Graphics().poly(flatten(shape)).fill(0xffffff);

  const texture = keepAlive(
    RenderTexture.create({
      width: w,
      height: h,
      resolution: renderer.resolution,
      antialias: true,
      label: 'dying-field',
    }),
  );

  const blur = new BlurFilter({ strength: style.glowBlur, quality: 8, resolution: 0.5 });
  g.filters = [blur];
  // 显式给 filterArea：不给的话它按图形的**实测边界**算，而这里要的是整块纹理
  g.filterArea = new Rectangle(0, 0, w, h);
  renderer.render({ container: g, target: texture, clear: true });
  blur.destroy();
  g.destroy();
  return texture;
}

/**
 * 把光**上半调滤镜**，得到黑色半调阴影。
 *
 * ```
 *   那片模糊的光 ──▶ HalftoneFilter(镂空模式) ──▶ 白色网点
 *                     纸色/墨色都设成白，
 *                     alpha 跟着覆盖率走（`alphaFromInk = 1`）
 * ```
 *
 * 输出是**预乘白点**：`rgb = white × 覆盖率`、`a = 覆盖率`。
 * 贴到精灵上再 `tint = 0x000000` —— tint 只乘 rgb，于是点变黑、alpha 不变。
 *
 * ⚠️ 用的是**背景那一级同一个** `HalftoneFilter`。这不是偷懒：
 *    阴影的网点和背景的网点必须是同一套语言（同样的 `sqrt(灰度)` 面积律、
 *    同样的边缘柔度），两套实现迟早会走偏。
 *
 * ⚠️ 节距和角度要和背景错开，否则两层网点叠出摩尔纹（见 `DEFAULT_DYING_STYLE`）。
 */
function bakeShadow(renderer: Renderer, field: RenderTexture, style: DyingStyle): RenderTexture {
  const texture = keepAlive(
    RenderTexture.create({
      width: field.width,
      height: field.height,
      resolution: renderer.resolution,
      antialias: true,
      label: 'dying-shadow',
    }),
  );

  const filter = new HalftoneFilter({
    resolution: [field.width, field.height],
    dotPitch: style.shadowPitch,
    dotAngle: style.shadowAngle,
    contrast: style.shadowContrast,
    /**
     * ⚠️ **和背景用同一个口径**（`FIELD_HALFTONE_BINARY`）：0 档要的是"加像素化
     * 之前的样子"，那片黑色半调阴影当时也是柔边的 —— 这里是**预渲染**的一张图，
     * 不跟着走的话，观众会看到一个柔边背景上贴着一块硬边阴影。
     */
    binary: halftoneBinaryOf(0) ? 1 : 0,
    // 镂空模式下纸色用不着（alpha 为 0 的地方看不见），但两个都给白
    paper: [1, 1, 1],
    ink: [1, 1, 1],
    alphaFromInk: 1,
  });

  const sprite = new Sprite(field);
  sprite.filters = [filter];
  // 显式给 filterArea：不给的话它按精灵的**实测边界**算，而这里要的是整块纹理
  sprite.filterArea = new Rectangle(0, 0, field.width, field.height);
  renderer.render({ container: sprite, target: texture, clear: true });

  filter.destroy();
  sprite.filters = [];
  sprite.destroy();
  return texture;
}
